import { Cached } from '@mui/icons-material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Grid, Box, Menu, MenuItem, SelectChangeEvent } from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { HiOutlineUpload } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  hasDownloadPermissions,
  IDownloadPermission,
  amplitude,
  csrfTokenCall,
} from 'wisestep-api';
import { WsButton, WsColors, WsLocationSearch, WsSelect, WsToggleObject } from 'wisestep-base';

import { ActionFilters } from '../../../../../components/filters/actionFilters';
import { sortOptions } from '../../../../../components/filters/components/AdvanceFilterHelper';
import WsMappingAdvanceFilters from '../../../../../components/filters/mappings';
import MappingsAdvanceFilterFormProps from '../../../../../components/filters/models/MappingAdvanceFiltersFormProps';
import WsSearchFilter from '../../../../../components/WsSearchFilter';
import { useWorkspaceContext } from '../../../../../context';
import { useUserContext } from '../../../../../context/UserContext/UserContext';
import { useCandidateAts, CandidateAtsAccess } from '../../../../../hooks/useCandidateAts';
import ErrorLogger from '../../../../../providers/ErrorLogger';
import { TalentPoolSearchRequest } from '../../../talentpool/components/search/TalentPoolSearchRequest';
import { toggleArray } from '../../../utils/CandidateHelper';

export interface MappingsSearchProps {
  onSearch: (data: TalentPoolSearchRequest) => void;
  selectedTabToggle?: (value: string | number) => void;
  onApplyFilters: (data: MappingsAdvanceFilterFormProps) => void;
  handleDownload: (type: string) => void;
  adavnceFilterdefaultValues: MappingsAdvanceFilterFormProps;
  searchFilterdefaultValues: TalentPoolSearchRequest;
  clearAdvanceFilters: () => void;
  onSort: (event: SelectChangeEvent) => void;
  onSearchScope: (value: string | number) => void;
}

const MappingsSearch = ({
  onSearch,
  onApplyFilters,
  handleDownload,
  adavnceFilterdefaultValues,
  searchFilterdefaultValues,
  onSort,
  clearAdvanceFilters,
  onSearchScope,
}: MappingsSearchProps) => {
  const navigate = useNavigate();
  const workspaceContext = useWorkspaceContext();
  const methods = useForm<TalentPoolSearchRequest>({ defaultValues: searchFilterdefaultValues });
  const { handleSubmit, setValue } = methods;
  const [open, setOpen] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<boolean>(false);
  const workspace = useWorkspaceContext();
  const [downloadAnchor, setDownloadAnchor] = useState<null | HTMLElement>(null);
  const openDownload = Boolean(downloadAnchor);

  const handleOpenDownloadOptions = (event: React.MouseEvent<HTMLElement>) =>
    setDownloadAnchor(event.currentTarget);
  const handleCloseDownloadOptions = () => setDownloadAnchor(null);

  const onClickDownload = (type: string) => {
    handleDownload(type);
    handleCloseDownloadOptions();
  };

  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');

  const user = useUserContext();

  const excludeDuplicate: WsToggleObject[] = [
    {
      label: 'Yes',
      value: 'true',
    },
    {
      label: 'No',
      value: 'false',
    },
  ];

  const clearAllFields = () => {
    methods.reset();
    clearAdvanceFilters();
  };
  useEffect(() => {
    const permissionData = async () => {
      const permissionResponse = await hasDownloadPermissions();
      const permissionResponseData: IDownloadPermission = permissionResponse.data;
      setPermissionState(permissionResponseData.CS_DOWNLOAD_CANDIDATE_LIST);
    };

    permissionData();
  }, []);

  const handleIndexChange = (value) => {
    setValue('searchScope', value);
    onSearchScope(value);
  };
  const isViewDownload = useCandidateAts(CandidateAtsAccess.PIPELINE_DOWNLOAD);

  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  return (
    <>
      <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
        <FormProvider {...methods}>
          <Box component="form" onSubmit={handleSubmit(onSearch)}>
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <WsSearchFilter
                className="pipeline-filters"
                name={'NativeSearchSearch'}
                showChildren={true}
                onClickFilter={() => setOpen(true)}
                showLeftActions={true}
                showDefaultRightActions={true}
                sortOptions={sortOptions}
                onSort={onSort}
                sortDefaultValue={'_score,desc'}
                clearAllFields={clearAllFields}
                searchScopeDefaultValue={searchFilterdefaultValues.searchScope}
                getSearchScopeIndex={handleIndexChange}
                leftActionsChildren={
                  <Grid container item pt={4.2} xs={12}>
                    <Grid item xs={12}>
                      <WsButton
                        variant="text"
                        onClick={() => {
                          amplitude('Pipeline_Search_Imp_Can', {
                            clickedOn: 'Import Candidates',
                            pageName: 'Mappings',
                            pageUrl: window.location.href,
                            workspaceName: workspace.name,
                            planType: workspace.planType,
                            interfaceType: 'Web',
                          });

                          navigate('/import-candidates');
                        }}
                        startIcon={<HiOutlineUpload color={WsColors.TheBlues500} />}
                      >
                        Import Candidates
                      </WsButton>
                      <WsButton
                        onClick={() =>
                          amplitude('Pipeline_Search_Refresh', {
                            clickedOn: 'Refresh',
                            pageName: 'Mappings',
                            pageUrl: window.location.href,
                            workspaceName: workspace.name,
                            planType: workspace.planType,
                            interfaceType: 'Web',
                          })
                        }
                        variant="text"
                        startIcon={<Cached />}
                        type="submit"
                      >
                        Refresh
                      </WsButton>
                      <ErrorBoundary
                        FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                      >
                        {permissionState &&
                          (workspaceContext?.owns ||
                            workspaceContext?.roles?.indexOf('ROLE_DOWNLOAD') !== -1) &&
                          isViewDownload && (
                            <WsButton
                              onClick={handleOpenDownloadOptions}
                              startIcon={<FileDownloadOutlinedIcon />}
                              variant="text"
                            >
                              Download
                            </WsButton>
                          )}
                      </ErrorBoundary>
                      <ErrorBoundary
                        FallbackComponent={({ error }) => <ErrorLogger error={error} />}
                      >
                        <ActionFilters source="Pipeline_Search_Action" />
                      </ErrorBoundary>
                      <Menu
                        anchorEl={downloadAnchor}
                        open={openDownload}
                        onClose={handleCloseDownloadOptions}
                      >
                        <MenuItem onClick={() => onClickDownload('csv')}>Download as CSV</MenuItem>
                        <MenuItem onClick={() => onClickDownload('xls')}>Download as XLS</MenuItem>
                      </Menu>
                    </Grid>
                  </Grid>
                }
              >
                <Grid container item xs={12} pt={2}>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={9}>
                      <WsLocationSearch
                        distanceSelectName="distance"
                        placeHolder="Search For Locations"
                        size="small"
                        showDistance={true}
                        name={'locations'}
                        multiSelect={true}
                        wisestepApi={true}
                        token={token}
                        authorization={auth}
                      />
                    </Grid>
                  </ErrorBoundary>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    <Grid item xs={3} pl={2} justifyContent="flex-end">
                      <WsSelect
                        label="Location Preferences"
                        name="candidateLocationPref"
                        selectArray={toggleArray}
                        defaultValue="Current or Preferred"
                      />
                    </Grid>
                  </ErrorBoundary>
                  <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
                    {user.userDTO?.email == 'kevin.s@avanceservices.us' && (
                      <Grid item xs={2} justifyContent="flex-end">
                        <WsSelect
                          label="Exclude Duplicates"
                          name="excludeDuplicateRecords"
                          selectArray={excludeDuplicate}
                          defaultValue="false"
                        />
                      </Grid>
                    )}
                  </ErrorBoundary>
                </Grid>
              </WsSearchFilter>
            </ErrorBoundary>
          </Box>
        </FormProvider>
      </ErrorBoundary>
      {open && (
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <WsMappingAdvanceFilters
            open={open}
            onApplyFilters={(data: MappingsAdvanceFilterFormProps) => onApplyFilters(data)}
            filterClose={() => setOpen(false)}
            defaultValues={adavnceFilterdefaultValues}
          />
        </ErrorBoundary>
      )}
    </>
  );
};
export default MappingsSearch;
